import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Milestones — READ-ONLY admin list of Large Projects milestones.
//
// Surfaces every milestone (project ref + title, company, amount, status,
// submitted/paid timestamps, and an escrow `held` signal) with filters by status
// and contract. There is NO money action here on purpose — milestone money
// movement (release / refund on a disputed milestone) stays in the Disputes
// admin (resolveMilestoneDispute). This page is for visibility only.
//
// Consumes besthand-api `adminListProjectMilestones` (returns `milestones`)
// through the `/lp-graphql` proxy via `lpApi`. Admin-gated server-side
// (requireAdmin). Style mirrors FundsHeld.js / CompanyProviders.js.
// ============================================================================

const Q_MILESTONES = `
  query ($status: String, $contractId: Int) {
    adminListProjectMilestones(status: $status, contractId: $contractId) {
      status errorMessage
      milestones {
        id projectContractId orderIndex name amount currency status
        isDownPayment startedAt submittedAt approvedAt paidAt createdAt
        projectRequestId projectTitle companyId companyName held
      }
    }
  }
`;

const STATUSES = [
  'ALL', 'PENDING', 'READY', 'IN_PROGRESS', 'SUBMITTED_FOR_REVIEW',
  'APPROVED', 'DISPUTED', 'PAID', 'CANCELLED',
];

// MONEY-PATH admin actions (admin-gated server-side via requireAdmin +
// gateLargeProjects). Release reuses the approveMilestone transfer path minus
// the client OTP; refund reuses refundMilestonePayment (+ reversal if released).
const M_RELEASE = `
  mutation ($milestoneId: String!, $reason: String) {
    adminReleaseMilestone(milestoneId: $milestoneId, reason: $reason) {
      status errorMessage
      milestone { id status paidAt approvedAt }
    }
  }
`;
const M_REFUND = `
  mutation ($milestoneId: String!, $reason: String, $amount: Float) {
    adminRefundMilestone(milestoneId: $milestoneId, reason: $reason, amount: $amount) {
      status errorMessage
      milestone { id status }
      payment { id amount refundedAmount stripeRefundId }
    }
  }
`;

const S = {
  page: { maxWidth: 1080, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' },
  select: { border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 12px', fontSize: 13, fontWeight: 600, background: '#fff', color: '#0f1214' },
  input: { border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 12px', fontSize: 13, width: 150 },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  totalPill: { background: '#0f1214', color: '#fff', borderRadius: 10, padding: '8px 14px', fontSize: 14, fontWeight: 800 },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '16px 20px', marginBottom: 12, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  name: { fontSize: 17, fontWeight: 800, margin: 0 },
  amount: { fontSize: 20, fontWeight: 800, color: '#0f1214', whiteSpace: 'nowrap' },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.5 },
  badge: (kind) => {
    const map = {
      PENDING: { bg: '#fff3d6', fg: '#946200' },
      READY: { bg: '#eef2ff', fg: '#3730a3' },
      IN_PROGRESS: { bg: '#e0f2fe', fg: '#075985' },
      SUBMITTED_FOR_REVIEW: { bg: '#fef3c7', fg: '#92400e' },
      APPROVED: { bg: '#e7f6ec', fg: '#137a37' },
      DISPUTED: { bg: '#fde7e8', fg: '#c2151c' },
      PAID: { bg: '#dcfce7', fg: '#166534' },
      CANCELLED: { bg: '#eef2f4', fg: '#5a6168' },
      DOWN: { bg: '#eef2ff', fg: '#3730a3' },
      HELD: { bg: '#fff7ed', fg: '#9a3412' },
    };
    const c = map[kind] || { bg: '#eef2f4', fg: '#5a6168' };
    return { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: 'nowrap' };
  },
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  actions: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  btnRelease: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnRefund: { background: '#fff', color: '#c2151c', border: '1px solid #f3c9cb', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

const fmtMoney = (v, cur) => (v == null ? '—' : `${cur && cur !== 'USD' ? `${cur} ` : '$'}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleString(); } catch (e) { return s; } };

function Milestones() {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [status, setStatus] = useState('ALL');
  const [contractId, setContractId] = useState('');
  const [busyId, setBusyId] = useState('');

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const variables = {};
      if (status && status !== 'ALL') variables.status = status;
      const cid = parseInt(contractId, 10);
      if (cid) variables.contractId = cid;
      const res = await lpApi(Q_MILESTONES, variables);
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.adminListProjectMilestones;
        if (env && env.status === 200) setMilestones(env.milestones || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load milestones: ${e.message}` });
    }
    setLoading(false);
  }, [status, contractId]);

  useEffect(() => { load(); }, [load]);

  const run = async (key, query, variables, opName, okText) => {
    setBusyId(key);
    setMsg(null);
    try {
      const res = await lpApi(query, variables);
      if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
      const env = res.data && res.data[opName];
      if (env && env.status >= 200 && env.status < 300) {
        setMsg({ ok: okText });
        await load();
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Action failed: ${e.message}` });
    }
    setBusyId('');
  };

  const release = (m) => {
    const ok = typeof window !== 'undefined' && window.confirm(
      `RELEASE milestone #${m.id} (${fmtMoney(m.amount, m.currency)}) to ${m.companyName || `company #${m.companyId}`}?\n\n`
      + 'This sends a REAL Stripe transfer of the escrowed funds to the company and marks the milestone PAID. '
      + 'It honors every release gate (lien waiver, jurisdiction, captured payment). This cannot be undone except by a refund.',
    );
    if (!ok) return;
    const reason = window.prompt(`Reason for releasing milestone #${m.id} (optional):`);
    if (reason === null) return;
    run(`r${m.id}`, M_RELEASE, { milestoneId: String(m.id), reason: reason.trim() || null }, 'adminReleaseMilestone', `Milestone #${m.id} released to the company.`);
  };

  const refund = (m) => {
    const ok = typeof window !== 'undefined' && window.confirm(
      `REFUND the client for milestone #${m.id} (${fmtMoney(m.amount, m.currency)})?\n\n`
      + 'This issues a REAL Stripe refund of the client charge (and reverses the company transfer if funds were already released). '
      + 'Leave the amount blank for a FULL refund, or enter a smaller amount for a partial refund.',
    );
    if (!ok) return;
    const raw = window.prompt(`Refund amount for milestone #${m.id} in USD (blank = full remaining):`, '');
    if (raw === null) return;
    let amount = null;
    if (raw.trim() !== '') {
      amount = Number(raw);
      if (!Number.isFinite(amount) || amount <= 0) { setMsg({ error: 'Refund amount must be a number greater than 0 (or blank for full).' }); return; }
    }
    const reason = window.prompt(`Reason for refunding milestone #${m.id} (optional):`);
    if (reason === null) return;
    run(`f${m.id}`, M_REFUND, { milestoneId: String(m.id), reason: reason.trim() || null, amount }, 'adminRefundMilestone', `Refund processed for milestone #${m.id}.`);
  };

  const totalAmount = milestones.reduce((sum, m) => sum + (Number(m.amount) || 0), 0);

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Milestones</h2>
      <div style={S.hint}>
        Large Projects — every contract milestone (amount, status, escrow held). Admins can release a
        submitted milestone to the company or refund the client (full or partial). These move REAL money.
      </div>

      <div style={S.toolbar}>
        <select style={S.select} value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUSES.map((st) => <option key={st} value={st}>{st === 'ALL' ? 'All statuses' : st.replace(/_/g, ' ')}</option>)}
        </select>
        <input
          style={S.input}
          type="number"
          placeholder="Contract #"
          value={contractId}
          onChange={(e) => setContractId(e.target.value)}
        />
        <button style={S.btnGhost} onClick={() => load()} disabled={loading}>
          {loading ? 'Refreshing…' : 'Apply / Refresh'}
        </button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{milestones.length} milestone{milestones.length === 1 ? '' : 's'}</span>
        {milestones.length > 0 && <span style={S.totalPill}>Total: {fmtMoney(totalAmount)}</span>}
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && (
        milestones.length === 0 ? (
          <div style={S.empty}>No milestones match this filter.</div>
        ) : milestones.map((m) => (
          <div style={S.card} key={m.id}>
            <div style={S.rowTop}>
              <div>
                <p style={S.name}>
                  {m.name || `Milestone #${m.id}`}
                  {m.isDownPayment ? <span style={{ ...S.badge('DOWN'), marginLeft: 8 }}>DOWN PAYMENT</span> : null}
                  {m.held ? <span style={{ ...S.badge('HELD'), marginLeft: 8 }}>HELD IN ESCROW</span> : null}
                </p>
                <div style={S.meta}>
                  {m.projectTitle ? `${m.projectTitle} · ` : ''}
                  {m.projectRequestId ? `Project #${m.projectRequestId} · ` : ''}
                  Contract #{m.projectContractId}
                  {m.orderIndex != null ? ` · Step ${m.orderIndex + 1}` : ''}
                </div>
                <div style={S.meta}>
                  {m.companyName ? `${m.companyName}` : (m.companyId ? `Company #${m.companyId}` : 'No company')}
                </div>
                <div style={S.meta}>
                  {m.submittedAt ? `Submitted ${fmtDate(m.submittedAt)}` : ''}
                  {m.approvedAt ? `${m.submittedAt ? ' · ' : ''}Approved ${fmtDate(m.approvedAt)}` : ''}
                  {m.paidAt ? `${(m.submittedAt || m.approvedAt) ? ' · ' : ''}Paid ${fmtDate(m.paidAt)}` : ''}
                  {!m.submittedAt && !m.approvedAt && !m.paidAt ? `Created ${fmtDate(m.createdAt)}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={S.amount}>{fmtMoney(m.amount, m.currency)}</div>
                <span style={S.badge(m.status)}>{(m.status || '—').replace(/_/g, ' ')}</span>
              </div>
            </div>
            {(m.status === 'SUBMITTED_FOR_REVIEW' || m.held || ['APPROVED', 'PAID', 'DISPUTED'].includes(m.status)) && (
              <div style={S.actions}>
                {m.status === 'SUBMITTED_FOR_REVIEW' && (
                  <button style={S.btnRelease} disabled={busyId === `r${m.id}`} onClick={() => release(m)}>
                    {busyId === `r${m.id}` ? '…' : 'Release to company'}
                  </button>
                )}
                {(m.held || ['SUBMITTED_FOR_REVIEW', 'APPROVED', 'PAID', 'DISPUTED'].includes(m.status)) && (
                  <button style={S.btnRefund} disabled={busyId === `f${m.id}`} onClick={() => refund(m)}>
                    {busyId === `f${m.id}` ? '…' : 'Refund client'}
                  </button>
                )}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default Milestones;
